/**
 * AddonPaymentSuccessPage — popup-only handler for add-on PayPal capture.
 *
 * This page is only ever opened inside a popup window by handleAddonPayment.
 * It captures the order, posts PAYPAL_SUCCESS to the opener, and closes immediately.
 * If somehow loaded outside a popup (e.g. PayPal blocked the popup and did a full-page
 * redirect), it still captures and then redirects to /chat with a toast.
 */
import React, { useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { LoadingSpinner } from '../components';
import { plansAPI, paymentsAPI } from '../services/api';

const AddonPaymentSuccessPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const hasRun = useRef(false);

    const orderId = useMemo(() => searchParams.get('token') || '', [searchParams]);

    useEffect(() => {
        if (hasRun.current) return;
        hasRun.current = true;

        const runCapture = async () => {
            if (!orderId) {
                if (window.opener) { window.close(); return; }
                toast.error('Missing PayPal order token.');
                navigate('/chat', { replace: true });
                return;
            }

            if (window?.history?.replaceState) {
                window.history.replaceState({}, '', '/payment/addon/success');
            }

            const addonPlanId = localStorage.getItem('pending_addon_plan_id');
            const processedKey = `addon_processed_${orderId}`;
            const processingKey = `addon_processing_${orderId}`;

            // Already processed — just post and close
            if (sessionStorage.getItem(processedKey) === 'true') {
                const cached = sessionStorage.getItem('addon_capture_result');
                let captureData = null;
                try { captureData = cached ? JSON.parse(cached) : null; } catch (_) {}
                _finish(captureData);
                return;
            }

            if (sessionStorage.getItem(processingKey) === 'true') {
                // Another tab is processing — wait briefly then close
                setTimeout(() => _finish(null), 1500);
                return;
            }

            // Attempt recovery if addonPlanId missing
            if (!addonPlanId) {
                try {
                    const lookup = await paymentsAPI.getInvoiceByPaypalOrder(orderId);
                    if (lookup?.data) { _finish(null); return; }
                } catch (_) {}
                if (window.opener) { window.close(); return; }
                toast.error('Payment session expired. Please contact support if symptoms persist.');
                navigate('/chat', { replace: true });
                return;
            }

            sessionStorage.setItem(processingKey, 'true');
            try {
                const response = await plansAPI.captureAddonPayPalOrder({
                    order_id: orderId,
                    addon_plan_id: parseInt(addonPlanId, 10),
                });
                const payload = response?.data || response;
                const captureData = {
                    order_id: orderId,
                    addon_plan_id: parseInt(addonPlanId, 10),
                    tokens: payload?.tokens || payload?.token_balance?.tokens_added || null,
                };
                sessionStorage.setItem('addon_capture_result', JSON.stringify(captureData));
                sessionStorage.setItem(processedKey, 'true');
                localStorage.removeItem('pending_addon_plan_id');
                _finish(captureData);
            } catch (error) {
                console.error('Addon capture failed:', error);
                if (window.opener) {
                    try {
                        window.opener.postMessage({ type: 'PAYPAL_ERROR', context: 'addon' }, '*');
                    } catch (_) {}
                    window.close();
                    return;
                }
                toast.error(error?.message || 'Payment verification failed.');
                navigate('/chat', { replace: true });
            } finally {
                sessionStorage.removeItem(processingKey);
            }
        };

        const _finish = (captureData) => {
            if (window.opener) {
                try {
                    window.opener.postMessage(
                        { type: 'PAYPAL_SUCCESS', context: 'addon', data: captureData },
                        '*'
                    );
                } catch (_) {}
                window.close();
            } else {
                // Full-page redirect fallback — chat page will refresh balance on load
                try {
                    sessionStorage.setItem('addon_purchase_pending_refresh', '1');
                } catch (_) { /* ignore */ }
                toast.success('Tokens added to your account!');
                navigate('/chat', { replace: true });
            }
        };

        runCapture();
    }, [orderId, navigate]);

    return <LoadingSpinner fullScreen text="Confirming your tokens…" />;
};

export default AddonPaymentSuccessPage;
